import { useEffect, useMemo, useRef, useState } from "react";
import { useApi } from "../../lib/api";
import { useT } from "../../i18n/I18nProvider";
import type { Contact } from "@shared/types";

// Searchable contact picker that replaces the plain <select> on the
// new-ticket form. Filters the in-memory list (the parent already
// loaded /api/contacts at mount) by case-insensitive substring on
// "first last" and on email. Once the operator types a query we
// also fire a debounced server search so contacts beyond the first
// 200 the parent loaded still show up.
export function ContactPicker({
  contacts,
  selectedId,
  onChange,
  placeholder,
}: {
  contacts: Contact[];
  selectedId: string;
  onChange: (id: string) => void;
  placeholder: string;
}) {
  const api = useApi();
  const t = useT();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [serverResults, setServerResults] = useState<Contact[] | null>(null);
  // Sticky memory of the last contact picked in this session. Needed
  // because the operator can pick a contact that only showed up
  // through the debounced server search — after pick() clears
  // serverResults the id would otherwise resolve to null against the
  // parent's (max-200) list and the display would go blank, which
  // reads as "click didn't select anything".
  const [lastPicked, setLastPicked] = useState<Contact | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const selected = useMemo<Contact | null>(() => {
    if (!selectedId) return null;
    return (
      contacts.find((c) => c.id === selectedId) ||
      serverResults?.find((c) => c.id === selectedId) ||
      (lastPicked?.id === selectedId ? lastPicked : null) ||
      null
    );
  }, [contacts, serverResults, lastPicked, selectedId]);

  // Debounced server search. Fires only when the dropdown is open
  // and the operator has typed something; clears its results when
  // the query goes back to empty so the dropdown falls back to the
  // local list.
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length === 0) {
      setServerResults(null);
      return;
    }
    const id = setTimeout(async () => {
      try {
        const res = await api.get<{ data: Contact[] }>(
          `/api/contacts?q=${encodeURIComponent(q)}`,
        );
        setServerResults(res.data);
      } catch {
        // keep showing whatever we had; the local filter still works
      }
    }, 200);
    return () => clearTimeout(id);
  }, [api, open, query]);

  // Click outside or ESC closes the dropdown.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // What to render in the dropdown. When the query is empty we show
  // every locally-loaded contact (first 200). When the operator is
  // typing we prefer the server result set (which can pull beyond
  // the first 200) and fall back to a local substring filter if
  // the network is slow.
  const candidates = useMemo<Contact[]>(() => {
    const q = query.trim().toLowerCase();
    if (q.length === 0) return contacts;
    const source = serverResults ?? contacts;
    return source.filter((c) => {
      const name = `${c.first_name ?? ""} ${c.last_name ?? ""}`.toLowerCase();
      const email = (c.email ?? "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [contacts, serverResults, query]);

  // Keep the keyboard cursor inside the visible candidates as the
  // list narrows.
  useEffect(() => {
    setHighlight((h) => Math.min(h, Math.max(0, candidates.length - 1)));
  }, [candidates.length]);

  function pick(c: Contact) {
    onChange(c.id);
    setLastPicked(c);
    setQuery("");
    setOpen(false);
    setServerResults(null);
  }

  function clear() {
    onChange("");
    setQuery("");
    setHighlight(0);
    inputRef.current?.focus();
    setOpen(true);
  }

  const displayWhenClosed = selected
    ? `${selected.first_name} ${selected.last_name}${selected.email ? ` — ${selected.email}` : ""}`
    : "";

  return (
    <div ref={containerRef} className="contact-picker">
      <div className="contact-picker-input-row">
        <input
          ref={inputRef}
          type="text"
          autoComplete="off"
          value={open ? query : displayWhenClosed}
          placeholder={placeholder}
          onFocus={() => { setOpen(true); setQuery(""); }}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setOpen(false);
              setQuery("");
              inputRef.current?.blur();
            } else if (e.key === "ArrowDown") {
              e.preventDefault();
              if (!open) setOpen(true);
              else setHighlight((h) => Math.min(h + 1, candidates.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setHighlight((h) => Math.max(h - 1, 0));
            } else if (e.key === "Enter") {
              if (open && candidates[highlight]) {
                e.preventDefault();
                pick(candidates[highlight]);
              }
            }
          }}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls="contact-picker-listbox"
        />
        {selectedId && (
          <button
            type="button"
            className="contact-picker-clear"
            aria-label={t("contacts.clearSelection")}
            title={t("contacts.clearSelection")}
            onClick={clear}
          >
            ✕
          </button>
        )}
      </div>
      {open && (
        <ul id="contact-picker-listbox" role="listbox" className="contact-picker-list">
          {candidates.length === 0 && (
            <li className="contact-picker-empty">{t("contacts.noMatches")}</li>
          )}
          {/* The parent loads at most 200 contacts up-front — if the
              operator's contact sits alphabetically outside that
              window it can't be found by scrolling. Nudge them to
              type instead, which fires the server search. */}
          {query.trim().length === 0 && candidates.length >= 200 && (
            <li className="contact-picker-hint">{t("contacts.typeToSearch")}</li>
          )}
          {candidates.map((c, i) => (
            <li key={c.id} role="option" aria-selected={i === highlight}>
              <button
                type="button"
                className={i === highlight ? "highlight" : ""}
                onClick={() => pick(c)}
                onMouseEnter={() => setHighlight(i)}
              >
                <span className="contact-picker-name">{c.first_name} {c.last_name}</span>
                {c.email && <span className="contact-picker-email">{c.email}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
